// ExecutiveAnswerState — THE canonical answer state (#4008 P0).
//
// WHY THIS EXISTS. The external judge's verdict on run p6-live-armed-b-20260728
// found the rendered answer contradicting itself before the CFO cleared the
// opening screen: one surface said "No package is recommended yet" while another
// said "Source-backed package recommended for commitment. $124,360,267"; a
// header said "0 gated" beside lever cards marked gated; an operating-income
// asker was told "You asked about EBITDA". None of those were wording bugs.
// They were FOUR SURFACES EACH DERIVING THE ANSWER INDEPENDENTLY.
//
// SHAPE (sol, #4008 adversarial solution review; captain ruling 2026-07-29).
// The first cut of this contract modelled the answer as a LINEAR LADDER of five
// rungs, most-committable first. Sol rejected that shape and the captain ruled
// sol wins, because the shape itself asserts something false: $305.8M and
// $182.3M are not *bigger rungs* than the committed floor, they are DIFFERENT
// LENSES on the same set of moves under different evidence standards. A ladder
// invites "the biggest number that renders wins"; a primary + lenses model makes
// that sentence unsayable.
//
// So the state carries:
//
//   * exactly ONE `primaryDecisionPackage`, selected by an explicit
//     receipt/evidence POLICY and never by amount size; and
//   * `referenceLenses[]`, each typed with a ROLE, a POSTURE, what it is
//     USABLE FOR, its EXACT member ids, its PROVENANCE, and its amount.
//
// Sol's semantic table for the judged run is the law this shape encodes:
//
//   $305.8M       planning_lens        net after the MAXIMUM deduction across
//                                      the pairs currently DECLARED to overlap;
//                                      includes modeled and gated inputs;
//                                      NEVER the primary.
//   $124.4M       candidate            selected target-gap amount whose own
//                                      payload lineage CONFLICTS (an
//                                      approved-source binding against a
//                                      source_pending twin). The lens carries
//                                      the conflict as its posture until the
//                                      solver reconciles it.
//   $182.3M       standalone_inventory non-additive. residual AND targetFill
//                                      are null, always.
//   committed     primary IF           receipt-backed under the ONE standard.
//   floor         receipt-backed       Carries exact members, receipt ids,
//                                      posture mix, and as-of.
//   case ledger   represented          "no receipted disjoint package" is the
//   = none                             honest result of the stricter path, not
//                                      an absence to hide.
//
// NOT A SIXTH PACKAGE OBJECT. The package spine is the EXISTING
// objective-package-solver contract — `ObjectivePackageSolution`, its coverage,
// its closed inclusion-state vocabulary, its overlap-receipt state. This file
// adds the PRESENTATION over that spine and nothing else; it never forks the
// packaging model and it never recomputes a figure the solver owns.
//
// NO-SUPPRESSION. This is reconciliation, not removal. A lens with no published
// amount stays in the list carrying a typed hold; a count of zero still renders;
// a metric that could not be named renders its hold. Nothing is hidden to make
// the page agree with itself.
//
// FOUNDER RULING (2026-07-28) + sol's PR review item 4. Every ABSENT/HOLD
// posture carries a TYPED reason and unlock, populated from the REAL evaluating
// condition — and the hold's `kind` distinguishes MISSING BUSINESS TRUTH from
// MISSING PLUMBING. "Not threaded into this emitter" must never render as "no
// approved plan is connected": a wrong unlock is worse than none, because it
// sends Finance to recreate facts the same answer packet already carries.

import type {
  ObjectivePackageAuthority,
  ObjectivePackageCoverage,
  ObjectivePackageOverlapReceiptState,
  ObjectivePackageSolution,
} from './objective-package-solver';

/**
 * THE ONE OVERLAP-RECEIPT STANDARD (adversarial review, #4008).
 *
 * The codebase carried two: the durable approved zero-overlap exclusion
 * receipts that the composite aggregator's certified floor consumes, and the
 * case-ledger's presentation pairs, which only ever emit OPEN 'undeclared'
 * lines. Reading different ones produced the judged contradiction directly —
 * governance armed a cell from live receipts while the package surfaces on the
 * same answer reported that no receipt existed. Every figure in this state
 * resolves its receipt state against this standard and no other.
 */
export const EXECUTIVE_ANSWER_OVERLAP_RECEIPT_STANDARD =
  'approved_overlap_exclusion_receipt_v1' as const;

export type ExecutiveAnswerOverlapReceiptStandard =
  typeof EXECUTIVE_ANSWER_OVERLAP_RECEIPT_STANDARD;

/**
 * WHAT KIND of absence this is. Sol's PR review, item 4: several holds on the
 * first cut described missing BUSINESS TRUTH when only THREADING was missing.
 *
 *  evidence_missing  — the governed fact genuinely does not exist on this
 *                      tenant/answer. Finance has real work to do.
 *  threading_pending — the fact EXISTS on this answer packet but has not been
 *                      wired into this projection yet. Engineering work; the
 *                      reader must not be sent to recreate it.
 *  policy_blocked    — the fact exists and is threaded, but a fail-closed policy
 *                      declines to publish it here (e.g. governance not armed).
 */
export type ExecutiveAnswerHoldKind =
  | 'evidence_missing'
  | 'threading_pending'
  | 'policy_blocked';

/**
 * Why a state is absent or held, and what arms it. Populated from the condition
 * that actually evaluated false — never a generic placeholder.
 */
export type ExecutiveAnswerHold = {
  kind: ExecutiveAnswerHoldKind;
  /** What condition is unmet, in executive words. No internal vocabulary. */
  reasonPlain: string;
  /** The specific step that arms it. An action a reader can take or assign. */
  unlockAction: string;
  /** Who owns that step, when the evaluating condition names an owner. */
  unlockOwner: string | null;
};

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * Where an amount came from, precisely enough that a reviewer can go and check
 * it without asking anyone. `includesModeledInputs` / `includesGatedInputs` are
 * the two facts the judged run buried: the $305.8M planning figure silently
 * carried a gated $6.2M workforce input, and the header counted "0 gated".
 */
export type ExecutiveAnswerProvenance = {
  /** The producer that published this amount. */
  producerId: string;
  /** Where it sits on the answer packet, for a reviewer to verify directly. */
  sourceFactPath: string;
  /** The as-of of the governed source behind it, when one is carried. */
  asOf: string | null;
  includesModeledInputs: boolean;
  includesGatedInputs: boolean;
  /** Approved receipt ids under the ONE standard. Empty when none apply. */
  receiptIds: readonly string[];
};

/**
 * Whether a figure's own payload lineage agrees with itself.
 *
 * Sol's correction to the external judge, point 2: on the judged run
 * `target_bridge_selected_package_usd` was bound as approved-source evidence at
 * `$124,360,267` (response.json:1494-1533) while its paired
 * `target_bridge_proven_disjoint_lever_usd` for the SAME amount was
 * `source_pending` / "Source confirmation needed" (:1539-1578), and the case
 * ledger said no receipted disjoint package existed (:6487-6489). The system
 * must not pick a sentence. It carries the conflict as the lens's posture until
 * the solver reconciles lineage and membership.
 */
export type ExecutiveAnswerLineage =
  | { state: 'consistent' }
  | {
      state: 'conflicted';
      /** The disagreement in executive words. */
      conflictPlain: string;
      /** The binding keys that disagree. Audit identity, not display copy. */
      conflictingBindingKeys: readonly string[];
      /** What reconciles it. */
      resolveAction: string;
    };

// ---------------------------------------------------------------------------
// Residual
// ---------------------------------------------------------------------------

/**
 * Residual against the required uplift, per basis.
 *
 * `withheld_not_additive` is the judge's defect #4. A non-additive set must not
 * display target coverage — and it must not display a FAKE ZERO either. The old
 * computation was `max(target - standalone, 0)`, so a $182.3M standalone set
 * against a $143.1M ask rendered "$0 residual", which reads as coverage achieved
 * AND silently swallowed the $39.2M surplus.
 *
 * Sol's PR review, item 6: the first cut then computed a `surplusOverTargetUsd`
 * from that same non-additive aggregate — arithmetic over a set the contract had
 * just declared un-summable. That is gone. The withheld state names the reason
 * and nothing else; if a surplus is ever to be disclosed it must come from a
 * basis that is additive.
 */
export type ExecutiveAnswerResidual = { basis: 'measured'; amountUsd: number };

/**
 * Why a lens carries NO residual. Sol's #4095 review, section 1: the semantic
 * table says a non-additive lens has "residual AND targetFill = null", and the
 * first cut emitted a non-null `{ basis: 'withheld_not_additive' }` object while
 * a test titled "neither a residual nor a target fill" asserted that very
 * object. The contract now matches its own table: `residual` is null, and the
 * REASON moves to its own field so nothing is suppressed.
 *
 * A renderer cannot mistake this for a number — it has no numeric field at all.
 */
export type ExecutiveAnswerResidualWithholding = {
  reasonCode: 'not_additive' | 'no_measured_basis';
  reasonPlain: string;
};

// ---------------------------------------------------------------------------
// Reference lenses
// ---------------------------------------------------------------------------

/**
 * A lens's role. CLOSED — code disposes. These are not rungs and carry no
 * ordering relative to the primary package; a `planning_lens` larger than the
 * primary is normal and says nothing about commitability.
 */
export const EXECUTIVE_REFERENCE_LENS_ROLES = [
  'planning_lens',
  'candidate',
  'standalone_inventory',
  'plan_of_record',
] as const;

export type ExecutiveReferenceLensRole =
  (typeof EXECUTIVE_REFERENCE_LENS_ROLES)[number];

/**
 * A lens's posture — the PERMISSION carried with the amount. A surface reads
 * this instead of inferring commitability from the size of a number.
 */
export type ExecutiveReferenceLensPosture =
  /** Planning and target-setting inside the company only. */
  | 'planning_only'
  /** Sized and selected, but its own payload lineage disagrees with itself. */
  | 'candidate_unresolved_lineage'
  /** Sized and selected, lineage clean, governance not yet armed. */
  | 'candidate_not_armed'
  /** Sized, lineage clean, governance armed — decidable, still not committed. */
  | 'candidate_decidable'
  /** Members are not proven separate. NEVER summed, never measured vs target. */
  | 'not_additive'
  /** The company's own approved plan, carried for reconciliation. */
  | 'plan_of_record'
  /** No amount published on this lens. `hold` states why and what arms it. */
  | 'absent';

/**
 * What a lens may be USED FOR. Closed vocabulary so a renderer cannot invent a
 * permission, and `usableForPlain` carries the sentence the reader sees.
 */
export const EXECUTIVE_LENS_USABLE_FOR = [
  'planning_and_target_setting',
  'decision_candidate_review',
  'per_move_sizing',
  'reconciliation_against_plan',
  'nothing_yet',
] as const;

export type ExecutiveLensUsableFor = (typeof EXECUTIVE_LENS_USABLE_FOR)[number];

/**
 * The reference lenses this contract can publish. Closed (#4008): a lens id
 * flows straight into `ExecutivePrimaryRejection.candidateId` and onto the wire,
 * so leaving it `string` left the "closed" vocabulary closed by convention only.
 */
export const EXECUTIVE_REFERENCE_LENS_IDS = [
  'selected_target_gap_candidate',
  'declared_overlap_planning_lens',
  'standalone_opportunity_inventory',
  'approved_plan_of_record',
] as const;

export type ExecutiveReferenceLensId =
  (typeof EXECUTIVE_REFERENCE_LENS_IDS)[number];

export type ExecutiveReferenceLens = {
  lensId: ExecutiveReferenceLensId;
  role: ExecutiveReferenceLensRole;
  /** Executive label. Never an internal basis key. */
  label: string;
  amountUsd: number | null;
  posture: ExecutiveReferenceLensPosture;
  usableFor: ExecutiveLensUsableFor;
  /** One plain sentence: what this amount may be used for. */
  usableForPlain: string;
  /** EXACT members. A lens that cannot name its members carries none and holds. */
  memberIds: readonly string[];
  provenance: ExecutiveAnswerProvenance | null;
  /**
   * Null when no residual is meaningful for this lens at all;
   * `withheld_not_additive` when the lens is non-additive. A lens whose posture
   * is `not_additive` may NEVER carry a measured residual or a target fill.
   */
  residual: ExecutiveAnswerResidual | null;
  /**
   * Non-null exactly when `residual` is null AND a reader would otherwise be
   * left wondering why. Silence is what the no-suppression law forbids; a null
   * with no stated reason is silence.
   */
  residualWithholding: ExecutiveAnswerResidualWithholding | null;
  targetFillPct: number | null;
  lineage: ExecutiveAnswerLineage;
  /** Non-null exactly when `posture` is 'absent'. */
  hold: ExecutiveAnswerHold | null;
};

// ---------------------------------------------------------------------------
// Primary decision package
// ---------------------------------------------------------------------------

/**
 * Why a lens was NOT admitted as the primary. Closed reason codes so the
 * selection is auditable and the same rejection cannot be worded two ways.
 */
export type ExecutivePrimaryRejectionReasonCode =
  | 'role_never_primary'
  | 'not_published'
  | 'not_receipt_backed'
  | 'lineage_conflict'
  | 'governance_not_armed'
  | 'not_additive'
  | 'threading_pending';

export type ExecutivePrimaryRejection = {
  /**
   * The lens id considered, or the candidate figure's id.
   *
   * #4008: this was `string`, which meant the "closed" vocabulary was closed by
   * convention only — nothing stopped an unlisted id reaching the wire, where the
   * public projection would opaque it and the redactor would rewrite it to
   * "source receipt in review". It is now the declared union.
   */
  candidateId: ExecutivePrimaryCandidateId;
  reasonCode: ExecutivePrimaryRejectionReasonCode;
  reasonPlain: string;
};

export const EXECUTIVE_PRIMARY_SELECTION_POLICY_ID =
  'receipt_backed_disjoint_package_v1' as const;

/**
 * Every closed-enum MACHINE value this contract can put on the wire, as one
 * enumerable set.
 *
 * #4008. These are not display strings and not internal identifiers: they are
 * the contract's own closed vocabularies, and a consumer keys posture logic off
 * them. The public workspace projection opaques anything that merely LOOKS like
 * an internal code (`^[a-z0-9]+([_:.-][a-z0-9]+)+$`), which matched every value
 * below and replaced it with an `ask-helix:workspace:value:<hash>` token; the
 * egress redactor then read that token as a source reference and rewrote all of
 * them to the single phrase "source receipt in review". Nine distinct machine
 * values collapsed into one display string. Exported here so the projection can
 * admit them by MEMBERSHIP in this contract rather than by pattern guesswork —
 * a value not declared here is still opaqued, so the fence does not widen.
 */
export const EXECUTIVE_PRIMARY_REJECTION_REASON_CODES = [
  'role_never_primary',
  'not_published',
  'not_receipt_backed',
  'lineage_conflict',
  'governance_not_armed',
  'not_additive',
  'threading_pending',
] as const;

/**
 * The reference-lens ids and package ids a rejection may name — every lens the
 * contract publishes, plus the two committed-floor package ids.
 */
export const EXECUTIVE_PRIMARY_CANDIDATE_IDS = [
  ...EXECUTIVE_REFERENCE_LENS_IDS,
  'committed_floor_including_modeled_admission',
  'committed_receipt_backed_package',
] as const;

export type ExecutivePrimaryCandidateId =
  (typeof EXECUTIVE_PRIMARY_CANDIDATE_IDS)[number];

/**
 * Every closed-enum machine value on this contract, as a LITERAL union — not
 * `string[]`. A widened array would let a typo through the admission set and
 * straight onto the wire, which is the failure mode this whole guard exists to
 * stop.
 */
export const EXECUTIVE_ANSWER_STATE_CLOSED_ENUM_VALUES = [
  EXECUTIVE_PRIMARY_SELECTION_POLICY_ID,
  EXECUTIVE_ANSWER_OVERLAP_RECEIPT_STANDARD,
  ...EXECUTIVE_PRIMARY_REJECTION_REASON_CODES,
  ...EXECUTIVE_PRIMARY_CANDIDATE_IDS,
  'executive_answer_state_v2',
  'objective_package_solution_v1',
  'objective_package_solver_v1',
  'objective_package_authority_v1',
  'selected_management_package',
  'evidence_backed',
  'source_receipted',
  'included',
  'candidate_only_held',
  'receipt_missing',
  'policy_excluded',
  'evidence_withheld',
  'non_additive_excluded',
  'management_package',
  'planning_lens',
  'candidate',
  'standalone_inventory',
  'plan_of_record',
  'planning_only',
  'candidate_unresolved_lineage',
  'candidate_not_armed',
  'candidate_decidable',
  'planning_and_target_setting',
  'decision_candidate_review',
  'per_move_sizing',
  'reconciliation_against_plan',
  'nothing_yet',
  'no_measured_basis',
  'evidence_posture',
  'evidence_missing',
  'policy_blocked',
  'in_primary',
  'in_planning_only',
  'anchor_blocked',
  'no_selected_members',
  'selected_member_not_additive',
  'selected_member_receipt_missing',
  'selected_member_source_unknown',
  'selected_modeled_disclosure_missing',
  'best_target_fit',
  'smallest_set',
  'blocked',
  'unknown',
  'modeled',
  'receipted',
  'missing',
  'committed',
  'absent',
  'consistent',
  'conflicted',
  'measured',
  'withheld',
  'governed',
  'strategic_advisory',
  'unlockable',
  'source',
  'formula',
  'owner',
  'overlap',
  'realization',
  'excluded',
  'go',
  'watch',
  'stop',
] as const;

export type ExecutiveAnswerStateClosedEnumValue =
  (typeof EXECUTIVE_ANSWER_STATE_CLOSED_ENUM_VALUES)[number];

/**
 * Machine-readable semantic slots that the browser legitimately consumes from
 * `ExecutiveAnswerState`.
 *
 * Some values are resolved from the active objective taxonomy (for example a
 * lever-family id), so they cannot all live in a compile-time enum. They remain
 * public only in these named contract slots and only when they use the strict
 * lower-snake token grammar below. Receipt ids, evidence refs, module ids,
 * formula keys, producer ids, and source paths are deliberately absent.
 */
export const EXECUTIVE_ANSWER_STATE_PUBLIC_MACHINE_KEYS = [
  'memberId',
  'memberIds',
  'leverFamilyId',
  'leverFamilyIds',
  'sequencedLeverFamilyIds',
  'askedMetricLabel',
  'answeredMetricLabel',
  'selectedCaseId',
  'scenarioMetricId',
] as const;

export type ExecutiveAnswerStatePublicMachineKey =
  (typeof EXECUTIVE_ANSWER_STATE_PUBLIC_MACHINE_KEYS)[number];

const EXECUTIVE_ANSWER_STATE_PUBLIC_MACHINE_KEY_SET: ReadonlySet<string> =
  new Set(EXECUTIVE_ANSWER_STATE_PUBLIC_MACHINE_KEYS);

/**
 * Static machine vocabularies keyed by their declared contract slot.
 *
 * This is deliberately not one global admission set. A value such as
 * `role_never_primary` is public in `selection.rejected[].reasonCode`, but it is
 * not public in `policyId`, `summary`, or any other field that happens to carry
 * the same string.
 */
export const EXECUTIVE_ANSWER_STATE_PUBLIC_STATIC_VALUES_BY_KEY = {
  overlapReceiptStandard: [EXECUTIVE_ANSWER_OVERLAP_RECEIPT_STANDARD],
  solverVersion: ['objective_package_solver_v1'],
  authorityVersion: ['objective_package_authority_v1'],
  packageVersion: ['objective_package_solution_v1'],
  packageId: ['selected_management_package'],
  anchorState: ['evidence_backed', 'blocked'],
  sourcePosture: ['source_receipted', 'modeled'],
  inclusionState: [
    'included',
    'candidate_only_held',
    'receipt_missing',
    'policy_excluded',
    'evidence_withheld',
    'non_additive_excluded',
  ],
  tier: ['governed', 'strategic_advisory', 'unlockable'],
  alternativeId: ['best_target_fit', 'smallest_set'],
  overlapReceiptState: ['receipted', 'missing'],
  lensId: [...EXECUTIVE_REFERENCE_LENS_IDS],
  role: ['planning_lens', 'candidate', 'standalone_inventory', 'plan_of_record'],
  usableFor: [
    'planning_and_target_setting',
    'decision_candidate_review',
    'per_move_sizing',
    'reconciliation_against_plan',
    'nothing_yet',
  ],
  countedInLensIds: [...EXECUTIVE_REFERENCE_LENS_IDS],
  headerCountingAxisId: ['evidence_posture'],
  axisId: ['source', 'formula', 'owner', 'overlap', 'realization'],
  inclusion: ['in_primary', 'in_planning_only', 'excluded', 'absent'],
  state: ['consistent', 'conflicted'],
  tone: ['go', 'watch', 'stop'],
  policyId: [EXECUTIVE_PRIMARY_SELECTION_POLICY_ID],
  candidateId: [...EXECUTIVE_PRIMARY_CANDIDATE_IDS],
  unit: ['USD', 'PCT', 'BPS', 'COUNT'],
} as const satisfies Readonly<Record<string, readonly string[]>>;

const EXECUTIVE_ANSWER_STATE_PUBLIC_STATIC_VALUE_SETS: Readonly<
  Record<string, ReadonlySet<string>>
> = Object.fromEntries(
  Object.entries(EXECUTIVE_ANSWER_STATE_PUBLIC_STATIC_VALUES_BY_KEY).map(
    ([key, values]) => [key, new Set<string>(values)],
  ),
);

const EXECUTIVE_PRIMARY_PACKAGE_POSTURES = new Set([
  'committed',
  'management_package',
  'absent',
]);
const EXECUTIVE_REFERENCE_LENS_POSTURES = new Set([
  'planning_only',
  'candidate_unresolved_lineage',
  'candidate_not_armed',
  'candidate_decidable',
  'not_additive',
  'plan_of_record',
  'absent',
]);
const EXECUTIVE_ANSWER_HOLD_KINDS = new Set([
  'evidence_missing',
  'threading_pending',
  'policy_blocked',
]);
const EXECUTIVE_PACKAGE_AUTHORITY_RESIDUAL_KINDS = new Set([
  'measured',
  'withheld',
]);
const EXECUTIVE_RESIDUAL_WITHHOLDING_REASON_CODES = new Set([
  'not_additive',
  'no_measured_basis',
]);
const EXECUTIVE_PACKAGE_AUTHORITY_WITHHELD_REASON_CODES = new Set([
  'anchor_blocked',
  'no_selected_members',
  'selected_member_not_additive',
  'selected_member_receipt_missing',
  'selected_member_source_unknown',
  'selected_modeled_disclosure_missing',
]);
const EXECUTIVE_PRIMARY_REJECTION_REASON_CODE_SET: ReadonlySet<string> =
  new Set(EXECUTIVE_PRIMARY_REJECTION_REASON_CODES);

function executiveAnswerStatePathEndsWith(
  path: readonly string[],
  suffix: readonly string[],
): boolean {
  if (suffix.length > path.length) return false;
  return suffix.every(
    (segment, index) => path[path.length - suffix.length + index] === segment,
  );
}

export function isExecutiveAnswerStatePublicMachineToken(
  key: string,
  value: string,
): boolean {
  if (!EXECUTIVE_ANSWER_STATE_PUBLIC_MACHINE_KEY_SET.has(key)) return false;
  const normalized = value.trim();
  return (
    normalized === value &&
    normalized.length > 0 &&
    normalized.length <= 160 &&
    /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/u.test(normalized)
  );
}

/**
 * Returns true only when a machine value is valid for the exact public
 * `ExecutiveAnswerState` slot named by `path`.
 *
 * `path` is relative to the state root and omits array indexes. Dynamic
 * member/lever/scenario identities use a strict lower-snake grammar; private
 * receipt, evidence, module, formula, producer, digest, and source-path
 * identities are intentionally absent and must be publicized as opaque tokens.
 */
export function isExecutiveAnswerStatePublicMachineValue(
  path: readonly string[],
  value: string,
): boolean {
  const key = path[path.length - 1] ?? '';
  if (!key || value.trim() !== value) return false;

  if (isExecutiveAnswerStatePublicMachineToken(key, value)) {
    return true;
  }

  if (key === 'schemaVersion') {
    return (
      (path.length === 1 && value === 'executive_answer_state_v2') ||
      (executiveAnswerStatePathEndsWith(path, ['packageSpine', 'schemaVersion']) &&
        value === 'objective_package_solution_v1')
    );
  }

  if (key === 'producerId') {
    return (
      (executiveAnswerStatePathEndsWith(path, [
        'packageSpine',
        'packageAuthority',
        'provenance',
        'producerId',
      ]) ||
        executiveAnswerStatePathEndsWith(path, [
          'packageAuthority',
          'provenance',
          'producerId',
        ])) &&
      value === 'objective_package_solver_v1'
    );
  }

  if (key === 'posture') {
    return path.includes('referenceLenses')
      ? EXECUTIVE_REFERENCE_LENS_POSTURES.has(value)
      : EXECUTIVE_PRIMARY_PACKAGE_POSTURES.has(value);
  }

  if (key === 'kind') {
    return path.includes('residualAgainstSelectedManagementPackage')
      ? EXECUTIVE_PACKAGE_AUTHORITY_RESIDUAL_KINDS.has(value)
      : EXECUTIVE_ANSWER_HOLD_KINDS.has(value);
  }

  if (key === 'basis') {
    return path.includes('residualAgainstSelectedManagementPackage')
      ? value === 'selected_management_package'
      : value === 'measured';
  }

  if (key === 'reasonCode') {
    if (path.includes('rejected')) {
      return EXECUTIVE_PRIMARY_REJECTION_REASON_CODE_SET.has(value);
    }
    if (path.includes('residualWithholding')) {
      return EXECUTIVE_RESIDUAL_WITHHOLDING_REASON_CODES.has(value);
    }
    if (
      path.includes('withheldReason') ||
      (path.includes('residualAgainstSelectedManagementPackage') &&
        path.includes('reason'))
    ) {
      return EXECUTIVE_PACKAGE_AUTHORITY_WITHHELD_REASON_CODES.has(value);
    }
    return false;
  }

  return (
    EXECUTIVE_ANSWER_STATE_PUBLIC_STATIC_VALUE_SETS[key]?.has(value) ?? false
  );
}

/**
 * TWO-DIRECTIONAL set equality, at compile time.
 *
 * #4008, sol's re-check. The first cut's "witnesses" were decorative: every one
 * of them was an array→union assignment, which only proves the array holds no
 * STRANGER. It cannot prove the array is COMPLETE — sol compiled a probe union
 * member (`__probe_missing_reason`) that appeared in no array and got zero
 * diagnostics. The runtime test was no better: it rebuilt its expected set from
 * the same arrays, so it could only ever agree with itself.
 *
 * `ExactlyTheSame` resolves to `never` unless the two types are mutually
 * assignable, so the `= true` initializer fails to compile in BOTH directions:
 * a union member missing from the array, and an array member missing from the
 * union. The tuple wrappers stop TypeScript distributing over the unions, which
 * is what makes the check exact rather than merely overlapping.
 */
export type ExactlyTheSame<Left, Right> = [Left] extends [Right]
  ? [Right] extends [Left]
    ? true
    : never
  : never;

// Direction 1 + 2 — the rejection reason codes.
const _REASON_CODES_EXACT: ExactlyTheSame<
  ExecutivePrimaryRejectionReasonCode,
  (typeof EXECUTIVE_PRIMARY_REJECTION_REASON_CODES)[number]
> = true;

// Direction 1 + 2 — the reference lens ids.
const _LENS_IDS_EXACT: ExactlyTheSame<
  ExecutiveReferenceLensId,
  (typeof EXECUTIVE_REFERENCE_LENS_IDS)[number]
> = true;

// Direction 1 + 2 — the candidate ids.
const _CANDIDATE_IDS_EXACT: ExactlyTheSame<
  ExecutivePrimaryCandidateId,
  (typeof EXECUTIVE_PRIMARY_CANDIDATE_IDS)[number]
> = true;

// Belt and braces, and a far better error message than `never` when it trips:
// a mapped type over the union whose keys must be exactly the array's members.
// Adding `__probe_missing_reason` to the union reports
// "Property '__probe_missing_reason' is missing in type ...".
const _REASON_CODE_COVERAGE: Record<
  ExecutivePrimaryRejectionReasonCode,
  true
> = {
  role_never_primary: true,
  not_published: true,
  not_receipt_backed: true,
  lineage_conflict: true,
  governance_not_armed: true,
  not_additive: true,
  threading_pending: true,
};

const _CANDIDATE_ID_COVERAGE: Record<ExecutivePrimaryCandidateId, true> = {
  selected_target_gap_candidate: true,
  declared_overlap_planning_lens: true,
  standalone_opportunity_inventory: true,
  approved_plan_of_record: true,
  committed_floor_including_modeled_admission: true,
  committed_receipt_backed_package: true,
};

// Every reason code and candidate id is ADMITTED by the projection set.
const _REASON_CODES_ADMITTED: readonly ExecutiveAnswerStateClosedEnumValue[] =
  EXECUTIVE_PRIMARY_REJECTION_REASON_CODES;
const _CANDIDATE_IDS_ADMITTED: readonly ExecutiveAnswerStateClosedEnumValue[] =
  EXECUTIVE_PRIMARY_CANDIDATE_IDS;

void _REASON_CODES_EXACT;
void _LENS_IDS_EXACT;
void _CANDIDATE_IDS_EXACT;
void _REASON_CODE_COVERAGE;
void _CANDIDATE_ID_COVERAGE;
void _REASON_CODES_ADMITTED;
void _CANDIDATE_IDS_ADMITTED;

export type ExecutivePrimarySelectionPolicyId =
  typeof EXECUTIVE_PRIMARY_SELECTION_POLICY_ID;

/**
 * THE SELECTION RECEIPT. Sol: "Primary package selection by explicit
 * evidence/receipt/decision policy, never amount size."
 *
 * FAIL CLOSED (sol's PR review, item 2): the policy admits nothing unless every
 * condition is affirmatively met. An empty governance roster, a missing panel,
 * an unmatched category key, a modeled facet, or a stale as-of all leave the
 * condition UNMET and therefore leave `conditionsUnmet` non-empty — which is
 * itself the reason no primary was selected. The first cut recommended unless
 * something proved it bad; this one refuses unless something proves it good.
 */
export type ExecutivePrimarySelection = {
  policyId: ExecutivePrimarySelectionPolicyId;
  /** The rule, stated once, in the words a reviewer can check it against. */
  ruleApplied: string;
  /** Conditions this run affirmatively met. */
  conditionsMet: readonly string[];
  /** Conditions this run did NOT meet. Empty exactly when a primary published. */
  conditionsUnmet: readonly string[];
  /** Every candidate considered and why it was not admitted. */
  rejected: readonly ExecutivePrimaryRejection[];
};

/**
 * The posture composition of the primary package's members. Sol: the state must
 * carry "exact members, receipt IDs, posture mix, as-of".
 *
 * On the re-armed live floor (`.codex/reports/data-readiness-20260728/floor-proof-latest.json`,
 * generated 2026-07-29T02:18:19Z) the published committed floor is
 * $79,829,791.31 = $67,829,791.31 receipted across two members plus a
 * $12,000,000 MODELED member. Under this policy the modeled member does not
 * enter the primary; it is disclosed here and carried as its own lens. That is
 * the difference between "the floor is $79.8M" and "the floor you can commit to
 * is $67.8M, with a $12.0M modeled top-up beside it".
 */
export type ExecutiveAnswerPostureMix = {
  receiptedMemberCount: number;
  receiptedAmountUsd: number | null;
  modeledMemberCount: number;
  modeledAmountUsd: number | null;
  /** The plain sentence disclosing the mix. Always rendered when non-zero. */
  mixDisclosurePlain: string | null;
};

export type ExecutivePrimaryDecisionPackage = {
  /** Null exactly when `posture` is 'absent'. */
  packageId: string | null;
  label: string;
  amountUsd: number | null;
  posture: 'committed' | 'management_package' | 'absent';
  /** EXACT members. Never a count without the ids. */
  memberIds: readonly string[];
  /** Approved receipt ids under the ONE standard. */
  receiptIds: readonly string[];
  postureMix: ExecutiveAnswerPostureMix;
  provenance: ExecutiveAnswerProvenance | null;
  overlapReceiptState: ObjectivePackageOverlapReceiptState | null;
  /** Named unresolved pairs when `overlapReceiptState` is 'missing'. */
  missingReceiptPairs: readonly string[];
  /** Projected from the solver's coverage; never recomputed here. */
  residual: ExecutiveAnswerResidual | null;
  targetFillPct: number | null;
  selection: ExecutivePrimarySelection;
  /** Non-null exactly when `posture` is 'absent'. */
  hold: ExecutiveAnswerHold | null;
};

// ---------------------------------------------------------------------------
// Metric / objective
// ---------------------------------------------------------------------------

/**
 * Which metric was ASKED and which was ANSWERED.
 *
 * `askedMetricLabel` is ALWAYS the literal asked metric. `mappingNote` is
 * non-null ONLY when a governed metric-preference mapping actually resolved the
 * asked metric onto a different answered metric. When no mapping happened, no
 * mapping sentence exists to render — the judge's defect #1 was a hardcoded
 * "You asked about EBITDA" rendering on an operating-income question that had no
 * mapping at all.
 */
export type ExecutiveAnswerMetric = {
  askedMetricLabel: string | null;
  answeredMetricLabel: string | null;
  /** Non-null iff asked !== answered. Reader-facing sentence. */
  mappingNote: string | null;
  /** Non-null when neither metric could be named from a governed source. */
  hold: ExecutiveAnswerHold | null;
};

export type ExecutiveAnswerObjectiveSlot = {
  amountUsd: number | null;
  hold: ExecutiveAnswerHold | null;
};

export type ExecutiveAnswerObjective = {
  baseline: ExecutiveAnswerObjectiveSlot;
  target: ExecutiveAnswerObjectiveSlot;
  requiredUplift: ExecutiveAnswerObjectiveSlot;
};

// ---------------------------------------------------------------------------
// Status ontology — NAMED AXES, never one badge
// ---------------------------------------------------------------------------

/**
 * ONE count source per AXIS. The judge's defect #2 was a "0 gated" header beside
 * gated lever cards, because the header counted the CARDS THAT RENDERED while
 * the cards came from the API's lever roster.
 *
 * Sol's finding #5 went further: "certified", "modeled", "gated", "armed" and
 * "accepted" are NOT ONE ENUM. Evidence posture, governance arming, package
 * eligibility and render visibility are four different questions about the same
 * roster. Collapsing them into one badge is what let a header report a count of
 * an axis nobody named. So the state carries named axes and NAMES THE ONE THE
 * HEADER COUNTS.
 */
export type ExecutiveAnswerCounts = {
  certified: number;
  modeled: number;
  gated: number;
  /** Levers held back from full display. A render fact, never a posture. */
  reviewLimited: number;
  total: number;
};

/**
 * The five governance facets the value-governance panel already models
 * (`flagship-brief.value-governance.ts`). Named axes, never flattened into one
 * universal certification badge.
 */
export const EXECUTIVE_GOVERNANCE_FACET_AXIS_IDS = [
  'source',
  'formula',
  'owner',
  'overlap',
  'realization',
] as const;

export type ExecutiveGovernanceFacetAxisId =
  (typeof EXECUTIVE_GOVERNANCE_FACET_AXIS_IDS)[number];

export type ExecutiveGovernanceAxisCount = {
  axisId: ExecutiveGovernanceFacetAxisId;
  /** Executive label for the axis. */
  label: string;
  live: number;
  modeled: number;
  notYetArmed: number;
  total: number;
};

export const EXECUTIVE_HEADER_COUNTING_AXIS_ID = 'evidence_posture' as const;

export type ExecutiveHeaderCountingAxisId =
  typeof EXECUTIVE_HEADER_COUNTING_AXIS_ID;

export type ExecutiveAnswerStatusOntology = {
  /** Evidence posture over the API's own lever roster. */
  evidencePosture: ExecutiveAnswerCounts;
  /** Five named governance axes. Empty when no panel resolved. */
  governanceFacetAxes: readonly ExecutiveGovernanceAxisCount[];
  /**
   * The axis the header's headline count reports. Declared so no surface can
   * count one axis and label it with another's vocabulary.
   */
  headerCountingAxisId: ExecutiveHeaderCountingAxisId;
  headerCountingAxisLabel: string;
};

// ---------------------------------------------------------------------------
// Workforce + scenario coherence
// ---------------------------------------------------------------------------

/**
 * ONE typed workforce state. The judged run rendered workforce as gated
 * (canvas:223-224), as excluded with no value counted (:1681), and as present in
 * the planning sum (:1908-1926) — three answers to one question, because three
 * producers each decided independently.
 */
export type ExecutiveWorkforceInclusion =
  | 'in_primary'
  | 'in_planning_only'
  | 'excluded'
  | 'absent';

export type ExecutiveWorkforceState = {
  inclusion: ExecutiveWorkforceInclusion;
  amountUsd: number | null;
  /** The lens ids whose amounts contain this value. Exhaustive. */
  countedInLensIds: readonly string[];
  /** The one sentence every surface renders about workforce inclusion. */
  statementPlain: string;
  hold: ExecutiveAnswerHold | null;
};

/**
 * Scenario selection, criteria-based. Sol: "the AI-investment-conversion
 * scenario should not be auto-promoted into an operating-income answer solely
 * because a module supplied it."
 */
export type ExecutiveScenarioSelection = {
  selectedCaseId: string | null;
  /** The declared criteria that selected it. Empty means NO case is starred. */
  criteriaApplied: readonly string[];
  /** The metric the sweep is about. */
  scenarioMetricId: string | null;
  /** True only when the sweep's metric matches the answered metric. */
  relevantToAnsweredMetric: boolean;
  /** True only when relevant AND criteria selected a case. */
  promotedIntoAnswer: boolean;
  hold: ExecutiveAnswerHold | null;
};

// ---------------------------------------------------------------------------
// Verdict + state
// ---------------------------------------------------------------------------

export type ExecutiveAnswerVerdictTone = 'go' | 'watch' | 'stop';

export type ExecutiveAnswerVerdict = {
  /** The one verdict sentence. Every verdict surface renders THIS string. */
  line: string;
  /** The figures the verdict rests on, in one sentence. */
  basis: string;
  tone: ExecutiveAnswerVerdictTone;
};

export const EXECUTIVE_ANSWER_STATE_SCHEMA_VERSION =
  'executive_answer_state_v2' as const;

export type ExecutiveAnswerStateSchemaVersion =
  typeof EXECUTIVE_ANSWER_STATE_SCHEMA_VERSION;

export type ExecutiveAnswerState = {
  schemaVersion: ExecutiveAnswerStateSchemaVersion;
  /** The single receipt standard every figure in this state resolved against. */
  overlapReceiptStandard: ExecutiveAnswerOverlapReceiptStandard;
  metric: ExecutiveAnswerMetric;
  objective: ExecutiveAnswerObjective;
  /**
   * The packaging spine, CONSUMED not forked: the solver's own solution, when
   * this answer produced one. Everything below is a projection of it.
   */
  packageSpine: ObjectivePackageSolution | null;
  /** The solver-owned four-slot package authority. Projected, never rebuilt. */
  packageAuthority: ObjectivePackageAuthority | null;
  /** The solver's coverage, echoed for surfaces that need it whole. */
  coverage: ObjectivePackageCoverage | null;
  /** EXACTLY ONE. Selected by policy, never by amount size. */
  primaryDecisionPackage: ExecutivePrimaryDecisionPackage;
  /** Typed lenses. NOT ordered rungs; no lens is ever "the answer". */
  referenceLenses: readonly ExecutiveReferenceLens[];
  status: ExecutiveAnswerStatusOntology;
  workforce: ExecutiveWorkforceState;
  scenario: ExecutiveScenarioSelection;
  verdict: ExecutiveAnswerVerdict;
};

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

export function executiveAnswerLens(
  state: ExecutiveAnswerState,
  lensId: string
): ExecutiveReferenceLens | null {
  return state.referenceLenses.find((lens) => lens.lensId === lensId) ?? null;
}

export function executiveAnswerLensesByRole(
  state: ExecutiveAnswerState,
  role: ExecutiveReferenceLensRole
): readonly ExecutiveReferenceLens[] {
  return state.referenceLenses.filter((lens) => lens.role === role);
}

/** True when a lens's posture forbids any additive measurement against target. */
export function executiveLensIsNonAdditive(lens: ExecutiveReferenceLens): boolean {
  return lens.posture === 'not_additive';
}

// ---------------------------------------------------------------------------
// Structural invariants
// ---------------------------------------------------------------------------

/**
 * Structural invariants a binding surface may assert. Returns plain-words
 * violations; an empty array means the state is internally consistent.
 *
 * These are the exact contradictions the judge and both adversarial reviews
 * found, expressed as code: one primary or none with a stated reason; a held
 * figure states why; a non-additive lens never shows coverage, a residual, or a
 * target fill; a candidate whose lineage conflicts is never a recommendation;
 * the mapping sentence exists only when the metrics actually differ; the header
 * counts the axis it names.
 */
export function executiveAnswerStateViolations(state: ExecutiveAnswerState): string[] {
  const violations: string[] = [];
  const primary = state.primaryDecisionPackage;

  // --- primary package -------------------------------------------------------
  if (primary.posture === 'absent') {
    if (primary.amountUsd !== null) {
      violations.push('the primary package is absent yet carries an amount');
    }
    if (primary.packageId !== null) {
      violations.push('the primary package is absent yet carries a package id');
    }
    if (primary.hold === null) {
      violations.push('no primary package was selected and no hold states why');
    }
    if (primary.selection.conditionsUnmet.length === 0) {
      violations.push('no primary package was selected yet the policy reports every condition met');
    }
  } else {
    if (primary.amountUsd === null) {
      violations.push('the primary package is published yet carries no amount');
    }
    if (primary.packageId === null) {
      violations.push('the primary package is published yet carries no package id');
    }
    if (primary.hold !== null) {
      violations.push('the primary package is published yet carries a hold');
    }
    if (primary.memberIds.length === 0) {
      violations.push('the primary package is published yet names no members');
    }
    if (primary.selection.conditionsUnmet.length > 0) {
      violations.push('a primary package published while the selection policy reports unmet conditions');
    }
    if (primary.overlapReceiptState !== 'receipted') {
      violations.push('the primary package is published without an approved overlap receipt');
    }
    if (primary.receiptIds.length === 0) {
      violations.push('the primary package is published yet cites no receipt id');
    }
    // Selection must never rest on amount size. A policy with no affirmatively
    // met condition cannot have admitted anything.
    if (primary.selection.conditionsMet.length === 0) {
      violations.push('a primary package published with no affirmatively met policy condition');
    }
  }
  if (
    primary.overlapReceiptState === 'missing' &&
    primary.missingReceiptPairs.length === 0
  ) {
    violations.push('the primary package reports a missing overlap receipt but names no pair');
  }
  if (state.packageAuthority !== null) {
    const authority = state.packageAuthority;
    const selected = authority.selectedManagementPackage;
    if (
      primary.posture === 'committed' &&
      authority.selectedEstimatedAddition.memberIds.length > 0
    ) {
      violations.push('the primary package is committed while modeled value is included');
    }
    if (primary.posture !== 'absent') {
      if (selected.amountValue === null) {
        violations.push('the primary package published while package authority withheld it');
      } else if (primary.amountUsd !== selected.amountValue) {
        violations.push('the primary package amount does not match package authority');
      }
      if (primary.memberIds.join('\u0000') !== selected.memberIds.join('\u0000')) {
        violations.push('the primary package members do not match package authority');
      }
      if (primary.receiptIds.join('\u0000') !== selected.receiptIds.join('\u0000')) {
        violations.push('the primary package receipt ids do not match package authority');
      }
      if (authority.residualAgainstSelectedManagementPackage.kind === 'measured') {
        if (
          primary.residual?.amountUsd !==
          authority.residualAgainstSelectedManagementPackage.residualGapValue
        ) {
          violations.push('the primary residual does not match package authority');
        }
        if (
          primary.targetFillPct !==
          authority.residualAgainstSelectedManagementPackage.targetFillPct
        ) {
          violations.push('the primary target fill does not match package authority');
        }
      } else {
        if (primary.residual !== null) {
          violations.push('the primary residual published while package authority withheld it');
        }
        if (primary.targetFillPct !== null) {
          violations.push('the primary target fill published while package authority withheld it');
        }
      }
    }
  }

  // --- reference lenses ------------------------------------------------------
  const seenLensIds = new Set<string>();
  for (const lens of state.referenceLenses) {
    if (seenLensIds.has(lens.lensId)) {
      violations.push(`reference lens ${lens.lensId} appears more than once`);
    }
    seenLensIds.add(lens.lensId);

    if (lens.posture === 'absent') {
      if (lens.amountUsd !== null) violations.push(`${lens.lensId} is absent yet carries an amount`);
      if (lens.hold === null) violations.push(`${lens.lensId} is absent yet states no reason`);
    } else {
      if (lens.amountUsd === null) violations.push(`${lens.lensId} is published yet carries no amount`);
      if (lens.hold !== null) violations.push(`${lens.lensId} is published yet carries a hold`);
      if (lens.provenance === null) {
        violations.push(`${lens.lensId} is published yet states no provenance`);
      }
    }
    if (executiveLensIsNonAdditive(lens)) {
      if (lens.residual !== null) {
        violations.push(`${lens.lensId} is not additive yet displays a measured residual`);
      }
      if (lens.targetFillPct !== null) {
        violations.push(`${lens.lensId} is not additive yet displays a target fill`);
      }
      // Null WITHOUT a reason is the suppression the law forbids.
      if (lens.residualWithholding === null) {
        violations.push(`${lens.lensId} withholds its residual without stating why`);
      }
    }
    if (lens.residual !== null && lens.residualWithholding !== null) {
      violations.push(`${lens.lensId} publishes a residual and a reason for withholding one`);
    }
    if (lens.role === 'standalone_inventory' && lens.posture !== 'absent' && lens.posture !== 'not_additive') {
      violations.push(`${lens.lensId} is a standalone inventory yet claims an additive posture`);
    }
    if (lens.posture === 'candidate_decidable' && lens.lineage.state === 'conflicted') {
      violations.push(`${lens.lensId} is decidable yet its own lineage conflicts`);
    }
    if (lens.lineage.state === 'conflicted' && lens.posture !== 'candidate_unresolved_lineage') {
      violations.push(`${lens.lensId} carries a lineage conflict that its posture does not state`);
    }
    // A lens is NEVER the answer. If one carries the primary's identity, the
    // shape has regressed into a ladder.
    if (primary.packageId !== null && lens.lensId === primary.packageId) {
      violations.push(`${lens.lensId} is both the primary package and a reference lens`);
    }
  }

  // --- metric ---------------------------------------------------------------
  const { askedMetricLabel, answeredMetricLabel, mappingNote } = state.metric;
  const metricsDiffer =
    askedMetricLabel !== null &&
    answeredMetricLabel !== null &&
    askedMetricLabel.trim().toLowerCase() !== answeredMetricLabel.trim().toLowerCase();
  if (mappingNote !== null && !metricsDiffer) {
    violations.push('a metric mapping sentence renders where the asked and answered metrics match');
  }
  if (mappingNote === null && metricsDiffer) {
    violations.push('the asked and answered metrics differ with no mapping sentence');
  }

  // --- status ontology -------------------------------------------------------
  const { certified, modeled, gated, reviewLimited, total } = state.status.evidencePosture;
  // Posture buckets partition the roster; an unrecognized status counts toward
  // none rather than being guessed. `reviewLimited` is a RENDER fact about that
  // same roster (held back from full display), not a fourth posture — it is
  // bounded by the total but never summed with the postures.
  if (certified + modeled + gated > total) {
    violations.push('posture counts exceed the lever total');
  }
  if (reviewLimited > total) {
    violations.push('review-limited count exceeds the lever total');
  }
  if (state.status.headerCountingAxisId !== EXECUTIVE_HEADER_COUNTING_AXIS_ID) {
    violations.push('the header counts an axis this state does not publish');
  }
  const axisIds = new Set(state.status.governanceFacetAxes.map((axis) => axis.axisId));
  if (axisIds.size !== state.status.governanceFacetAxes.length) {
    violations.push('a governance axis is published more than once');
  }

  // --- workforce ------------------------------------------------------------
  if (state.workforce.inclusion === 'absent' && state.workforce.amountUsd !== null) {
    violations.push('workforce is absent yet carries an amount');
  }
  if (state.workforce.inclusion === 'excluded' && state.workforce.countedInLensIds.length > 0) {
    violations.push('workforce is excluded yet is counted inside a published lens');
  }
  if (
    state.workforce.inclusion === 'in_planning_only' &&
    primary.memberIds.some((id) => state.workforce.countedInLensIds.includes(id))
  ) {
    violations.push('workforce is planning-only yet sits inside the primary package');
  }

  // --- scenario -------------------------------------------------------------
  if (state.scenario.promotedIntoAnswer && !state.scenario.relevantToAnsweredMetric) {
    violations.push('a scenario was promoted into an answer framed on a different measure');
  }
  if (state.scenario.promotedIntoAnswer && state.scenario.criteriaApplied.length === 0) {
    violations.push('a scenario was promoted with no declared selection criteria');
  }
  if (state.scenario.selectedCaseId !== null && state.scenario.criteriaApplied.length === 0) {
    violations.push('a scenario case is starred with no declared selection criteria');
  }

  return violations;
}
